'use client';

import { useEffect, useRef } from 'react';
import { useRouter } from 'next/router';
import { useAnalytics } from '@/hooks/useAnalytics';
import { useSession } from '@/context/SessionContext';
import gTagConstants from '@/constants/gEvenets.constants';

/* ------------------------------------------------------------------ */
/*  Types                                                             */
/* ------------------------------------------------------------------ */
interface PageViewEventParams {
  batch_ordering_id: number;
  batch_page_id: number;
  campaign: string;
  campaign_id: string;
  content: string;
  medium: string;
  source: string;
  term: string;
  ga_session_id: string;
  ga_session_number: string;
  page_location: string;
  page_referrer: string;
  page_title: string;
  device_category: string;
  session_id: string;
  page_type: string;
  engagement_time_sec: number;
  entrances: number;
  sku?: string;
}

/* ------------------------------------------------------------------ */
/*  Component                                                         */
/* ------------------------------------------------------------------ */
export default function PageViewEvent() {
  const router = useRouter();
  const session = useSession();

  const {
    utmParams,
    pageParams,
    getDeviceCategory,
    getNextBatchOrderingId,
    getBatchPageId,
    getGASessionId,
    getGASessionNumber,
    skuSlug,
    getPageType,
  } = useAnalytics();

  // Track the current path to prevent duplicate events
  const currentPathRef = useRef<string>(
    typeof window !== 'undefined' ? window.location.pathname : ''
  );
  // Track time for engagement calculation
  const viewStartRef = useRef<number>(Date.now());
  // Track if we've already fired the initial page view
  const initialFiredRef = useRef<boolean>(false);

  /* ------------------------ Helpers ------------------------------------ */
  // Session storage key format
  const storageKey = `pv_${getGASessionId()}`;

  // Check if this is the first page view of the GA4 session
  const isFirstPageOfSession = (): boolean =>
    typeof window !== 'undefined' && !localStorage.getItem(storageKey);

  // Mark session as started in localStorage
  const setSessionFlag = () => {
    try {
      localStorage.setItem(storageKey, '1');
    } catch {
      /* Ignore localStorage errors in incognito mode */
    }
  };

  // Build params and send the event if not duplicate
  function firePageView(isInitialLoad = false) {
    // Skip if we're not in browser environment
    if (typeof window === 'undefined') return;

    // Get current path
    const currentPath = window.location.pathname;

    // For non-initial loads, skip if firing for the same path
    if (!isInitialLoad && currentPath === currentPathRef.current) {
      return;
    }

    // Update the current path ref
    currentPathRef.current = currentPath;

    // Calculate engagement time (0 for initial page view)
    const engagementTimeSec = isInitialLoad
      ? 0
      : Math.round((Date.now() - viewStartRef.current) / 1000);

    const params: PageViewEventParams = {
      batch_ordering_id: getNextBatchOrderingId(),
      batch_page_id: getBatchPageId(),
      campaign: utmParams.campaign || 'not_set',
      campaign_id: utmParams.campaign_id || 'not_set',
      content: utmParams.content || 'not_set',
      medium: utmParams.medium || 'not_set',
      source: utmParams.source || 'not_set',
      term: utmParams.term || 'not_set',
      ga_session_id: getGASessionId(),
      ga_session_number: getGASessionNumber(),
      page_location: pageParams.page_location,
      page_referrer: pageParams.page_referrer,
      page_title: pageParams.page_title,
      device_category: getDeviceCategory(),
      session_id: session.sessionToken || '',
      page_type: getPageType(),
      sku: skuSlug || undefined,
      engagement_time_sec: engagementTimeSec,
      entrances: isFirstPageOfSession() ? 1 : 0,
    };

    // Mark session started
    setSessionFlag();

    // Reset engagement timer
    viewStartRef.current = Date.now();

    // Send the event through available tracking APIs
    if (window.gtag) {
      window.gtag('event', gTagConstants.eventKeys.page_view, params);
    } else if (window.dataLayer) {
      window.dataLayer.push({ event: gTagConstants.eventKeys.page_view, ...params });
    }

    if (isInitialLoad) {
      initialFiredRef.current = true;
    }
  }

  /* ---------------- First fire (cold load) --------------------- */
  useEffect(() => {
    // Only fire initial page view once
    if (!initialFiredRef.current) {
      firePageView(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* --------------- Fire on SPA navigation ---------------------- */
  useEffect(() => {
    const handleRouteChange = (url: string) => {
      // Extract pathname without query params
      const urlObj = new URL(url, window.location.origin);
      const newPath = urlObj.pathname;

      // Only fire if we're actually changing paths
      if (newPath !== currentPathRef.current) {
        firePageView(false);
      }
    };

    router.events.on('routeChangeComplete', handleRouteChange);
    return () => router.events.off('routeChangeComplete', handleRouteChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.events]);

  return null; // Side effects only
}
